import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import CaseEventService from "services/CaseEvent";
import { bboxFor, eventsToGeoJson } from "components/CaseMap/helpers";

// A single machine on a busy case can carry tens of thousands of events, which
// no map can usefully draw. The overlay plots this many and says so.
export const MAP_EVENT_LIMIT = 2000;

const emptyGeoJson = { type: "FeatureCollection", features: [] };

/**
 * The events belonging to the machines whose overlay is showing.
 *
 * `machineIds` are the external identifiers the case-events table filters on,
 * not machine primary keys.
 */
export function useCaseMapEvents({ caseModel, machineIds, locationSource }) {
  const isEnabled =
    !!machineIds.length && !!caseModel?.case_id && !!caseModel?.product_line_id;

  const { data: eventsPage, isFetching } = useQuery({
    // The location source only decides which column is read, so it stays out of
    // the key — toggling WiFi/IP re-projects what is already cached.
    //
    // The product line does belong in it: a case_id is external and unique only
    // within its product line, so without it two cases could share a cache
    // entry and one would be served the other's events.
    queryKey: [
      "case-map-events",
      caseModel?.case_id,
      caseModel?.product_line_id,
      machineIds,
    ],
    queryFn: () =>
      CaseEventService.getTable({
        case_id: caseModel.case_id,
        product_line_id: caseModel.product_line_id,
        machine_id: machineIds.join(","),
        page: 1,
        per_page: MAP_EVENT_LIMIT,
      }),
    enabled: isEnabled,
  });

  const events = useMemo(() => eventsPage?.data ?? [], [eventsPage]);

  const { geoJson, eventBbox, plottedCount } = useMemo(() => {
    if (!events.length) {
      return {
        geoJson: emptyGeoJson,
        eventBbox: null,
        plottedCount: 0,
      };
    }
    const geoJson = eventsToGeoJson(events, locationSource);
    const eventBbox = bboxFor(geoJson);
    return {
      geoJson,
      eventBbox,
      // One feature is one coordinate, and co-located events are collapsed into
      // it, so the events plotted have to be summed rather than counted.
      plottedCount: geoJson.features.reduce(
        (total, feature) => total + feature.properties.count,
        0
      ),
    };
  }, [events, locationSource]);

  const total = eventsPage?.total ?? 0;

  return {
    eventBbox,
    geoJson,
    isLoading: isEnabled && isFetching,
    plottedCount,
    // Both caps matter to the analyst: the page limit, and events that carry no
    // coordinate for the active source.
    total,
    isTruncated: total > events.length,
  };
}
